/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { InlineNotification, Typography } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ProfileViewerCard from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileViewerCard';
import HighlightsSectionBody from 'bundles/account-profile/components/recruiting-profile-viewer/components/highlights/HightlightsSectionBody';
import RestrictHiringSolutionsGate from 'bundles/account-profile/components/recruiting-profile-viewer/utils/RestrictHiringSolutionsGate';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';

import _t from 'i18n!nls/account-profile';

type PropsFromCaller = {
  customCss?: Interpolation<Theme>;
};

type Props = PropsFromCaller;

const styles = {
  sectionTitle: () => css`
    margin-bottom: var(--cds-spacing-200);
  `,
};

const HighlightsSection = ({ customCss }: Props) => {
  const { profileState = {}, isProfileLoading } = useContext(ProfileContext);
  const { educationHistory, courseraHistory } = profileState;

  const latestEducation = educationHistory?.[0];

  // For empty state, do not render highlights section
  if (
    !isProfileLoading &&
    !courseraHistory?.completedProfessionalCertificates?.length &&
    !courseraHistory?.completedSpecializations?.length &&
    (!educationHistory?.length || !latestEducation?.isCurrentlyAStudent)
  ) {
    return null;
  }

  return (
    <ProfileViewerCard customCss={customCss}>
      <Typography variant="h2semibold" data-testid="highlights-section-title" css={styles.sectionTitle}>
        {_t('Highlights')}
      </Typography>
      <HighlightsSectionBody />
    </ProfileViewerCard>
  );
};

const ErrorBoundHighlightsSection = (props: Props) => {
  return (
    <ErrorBoundaryWithTagging
      componentName="HighlightsSection"
      errorComponent={
        <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
          {_t('There has been an error in the highlights section. Our development team will be alerted.')}
        </InlineNotification>
      }
    >
      <RestrictHiringSolutionsGate>
        <HighlightsSection {...props} />
      </RestrictHiringSolutionsGate>
    </ErrorBoundaryWithTagging>
  );
};

export default ErrorBoundHighlightsSection;
